import { assign, setup } from 'xstate';
import { PAYMENT_SELECTION_STATES } from './states';
import { PaymentSelectionContext, PaymentSelectionEvent } from './types';

const EMPTY_SELECTION: PaymentSelectionContext = {
  radioId: null,
  paymentType: null,
  existingPaymentMethod: null,
  newCheckoutComCard: null,
  newPaypal: null,
  newApplePay: null,
  newSepa: null,
};

/**
 * Owns which payment method is selected on the checkout payment page (Flow).
 * Flat by design: exactly one method is selected at a time, every SELECT_* is
 * accepted from any state, and each entry resets the other selection fields.
 * Selecting a stored card in the Checkout.com widget maps to CLEAR -> idle.
 * Actions are defined inline on their single transition: each is only ever
 * used once, and inlining lets TypeScript narrow `event` to that transition's
 * event type without a runtime `event.type !== 'X'` guard.
 */
export const paymentSelectionMachine = setup({
  types: {
    context: {} as PaymentSelectionContext,
    events: {} as PaymentSelectionEvent,
  },
}).createMachine({
  id: 'payment-selection',
  initial: PAYMENT_SELECTION_STATES.IDLE,
  context: { ...EMPTY_SELECTION },
  on: {
    SELECT_EXISTING: {
      target: `.${PAYMENT_SELECTION_STATES.EXISTING_SELECTED}`,
      actions: assign(({ event }) => ({
        radioId: event.radioId,
        paymentType: event.paymentType,
        existingPaymentMethod: event.paymentMethod,
        newCheckoutComCard: null,
        newPaypal: null,
        newApplePay: null,
        newSepa: null,
      })),
    },
    SELECT_NEW_CARD: {
      target: `.${PAYMENT_SELECTION_STATES.NEW_CARD_SELECTED}`,
      actions: assign(({ event }) => ({
        radioId: event.radioId,
        paymentType: event.paymentType,
        existingPaymentMethod: null,
        newCheckoutComCard: {
          paymentMethodCode: event.radioId,
          isReady: false,
          shouldSaveCardForFutureUse: false,
        },
        newPaypal: null,
        newApplePay: null,
        newSepa: null,
      })),
    },
    SELECT_PAYPAL: {
      target: `.${PAYMENT_SELECTION_STATES.PAYPAL_SELECTED}`,
      actions: assign(({ event }) => ({
        radioId: event.radioId,
        paymentType: event.paymentType,
        existingPaymentMethod: null,
        newCheckoutComCard: null,
        newPaypal: event.paypalMethod,
        newApplePay: null,
        newSepa: null,
      })),
    },
    SELECT_APPLE_PAY: {
      target: `.${PAYMENT_SELECTION_STATES.APPLE_PAY_SELECTED}`,
      actions: assign(({ event }) => ({
        radioId: event.radioId,
        paymentType: event.paymentType,
        existingPaymentMethod: null,
        newCheckoutComCard: null,
        newPaypal: null,
        newApplePay: {
          paymentMethodCode: event.radioId,
          shouldSaveCardForFutureUse: false,
        },
        newSepa: null,
      })),
    },
    SELECT_SEPA: {
      target: `.${PAYMENT_SELECTION_STATES.SEPA_SELECTED}`,
      actions: assign(({ event }) => ({
        radioId: event.radioId,
        paymentType: event.paymentType,
        existingPaymentMethod: null,
        newCheckoutComCard: null,
        newPaypal: null,
        newApplePay: null,
        newSepa: { paymentMethodCode: event.radioId },
      })),
    },
    CLEAR: {
      target: `.${PAYMENT_SELECTION_STATES.IDLE}`,
      actions: assign(() => EMPTY_SELECTION),
    },
  },
  states: {
    [PAYMENT_SELECTION_STATES.IDLE]: {},
    [PAYMENT_SELECTION_STATES.EXISTING_SELECTED]: {},
    [PAYMENT_SELECTION_STATES.NEW_CARD_SELECTED]: {
      on: {
        UPDATE_NEW_CARD: {
          actions: assign(({ event }) => ({
            newCheckoutComCard: event.card,
          })),
        },
      },
    },
    [PAYMENT_SELECTION_STATES.PAYPAL_SELECTED]: {},
    [PAYMENT_SELECTION_STATES.APPLE_PAY_SELECTED]: {},
    [PAYMENT_SELECTION_STATES.SEPA_SELECTED]: {},
  },
});
